import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import PageContainer from '@deliveroo/consumer-component-library/components/PageContainer';
import Heading from '@deliveroo/consumer-component-library/components/Heading';
import HeroShape from '../HeroShape';
import styles from './styles.scss';

export const HERO_CUISINES = {
  burger: 'Burger',
  'fried-chicken': 'Fried Chicken',
  kebab: 'Kebab',
  poke: 'Poké',
  sushi: 'Sushi',
  taco: 'Taco',
  'bubble-tea': 'Bubble Tea',
  pizza: 'Pizza',
  salad: 'Salad',
  'chow-mein': 'Chow Mein',
  dumplings: 'Dumplings',
};
export const HERO_CUISINES_BY_COUNTRY = {
  ae: ['pizza'],
  tw: ['bubble-tea'],
  kw: ['taco'],
  it: ['burger'],
  uk: ['taco'],
  nl: ['burger'],
  fr: ['burger'],
  be: ['burger'],
  ie: ['burger'],
  de: ['pizza'],
  es: ['pizza'],
  sg: ['chow-mein'],
  hk: ['dumplings'],
  au: ['burger'],
  fish: ['sushi'], // obviously
};

/**
 * A Homepage Hero component for the new Food Freedom campaign, with associated
 * cuisine images.
 */
export default function HomepageHeroCuisineImage({
  headerComponent,
  headerRef,
  headingText,
  cuisineImage,
  heroShapeColor,
  savedAddressCount,
  isAppBannerShown,
  isAppBannerCollapsed,
  showCondensedMobileLayout,
  overlapContent,
  children,
}) {
  const heroClassNames = classnames(styles.header, styles[cuisineImage], {
    [styles.appBanner]: isAppBannerShown,
    [styles.appBannerCollapsed]: isAppBannerCollapsed,
    [styles.condensedLayout]: showCondensedMobileLayout,
  });

  const heroImageContainerClasses = classnames(styles.heroImageContainer, styles[cuisineImage], {
    [styles.overlap]: overlapContent,
  });

  return (
    <HeroShape
      color={heroShapeColor}
      savedAddressCount={savedAddressCount}
      showCondensedMobileLayout={showCondensedMobileLayout}
    >
      <div className={heroClassNames}>
        {headerComponent}
        <PageContainer fullWidth="always">
          <div className={styles.wrapper} ref={headerRef}>
            <PageContainer>
              <div className={styles.content}>
                <div className={styles.heading}>
                  <Heading
                    size="large"
                    level={1}
                    color="white"
                    spacing={{ mobile: '0 0 large 0', tablet: '0 0 x-large 0' }}
                  >
                    <span className={styles.headingContent}>{headingText}</span>
                  </Heading>
                </div>
                <div className={styles.children}>{children}</div>
              </div>
            </PageContainer>
            {showCondensedMobileLayout && <div className={heroImageContainerClasses} />}
          </div>
        </PageContainer>
      </div>
    </HeroShape>
  );
}

HomepageHeroCuisineImage.propTypes = {
  /**
   * Nav Header component — usually supplied by the parent container
   */
  headerComponent: PropTypes.node.isRequired,

  /**
   * A callback function to supply a ref to the header. Required for
   * LocationSearch to be able to scroll to the top of the page via event
   * handlers.
   */
  headerRef: PropTypes.func.isRequired,

  /**
   * Heading text within the hero.
   */
  headingText: PropTypes.string.isRequired,

  /**
   * The cuisine image to show inside the hero.
   */
  cuisineImage: PropTypes.oneOf(Object.keys(HERO_CUISINES)).isRequired,

  /**
   * The colour of the hero shape.
   */
  heroShapeColor: PropTypes.string.isRequired,

  /**
   * Number of saved addresses being passed through to the address search /
   * location search child component, if any.
   */
  savedAddressCount: PropTypes.number,

  /**
   * Is the app banner currently being shown? (affects additional body padding)
   */
  isAppBannerShown: PropTypes.bool.isRequired,

  /**
   * Has the app banner been collapsed (after previously being shown)
   */
  isAppBannerCollapsed: PropTypes.bool.isRequired,

  /**
   * Are the condensed mobile layout changes enabled (MVT-117)
   */
  showCondensedMobileLayout: PropTypes.bool,

  /**
   * Toggle a class on the hero image to overlap locationSearch. It needs to overlap for
   * MVT-1172 varient in it's default state, then stop overlapping when there are
   * autocomplete suggestions.
   */
  overlapContent: PropTypes.bool,

  /**
   * Child nodes, typically the LocationSearchContainer component
   */
  children: PropTypes.node.isRequired,
};

HomepageHeroCuisineImage.defaultProps = {
  savedAddressCount: 0,
  showCondensedMobileLayout: false,
  overlapContent: false,
};
